// src/services/notification.service.ts
// Unified notification service for leads and appointments
import { collection, doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { projectService } from './project.service';
import { CalendarService } from './calendar.service';
import { Lead } from '../types/lead.types';
import { Appointment } from '../types/schedule.types';
import { formatTime, formatTimeRange } from '../utils/dateUtils';

export interface LeadNotificationData {
  leadId: string;
  leadName: string;
  projectId: string;
  projectName?: string;
  capturedByName: string;
  capturedById: string;
  phone?: string;
  email?: string;
}

export interface AppointmentNotificationData {
  appointmentId: string;
  leadId: string;
  leadName: string;
  projectId: string;
  projectName?: string;
  date: string;
  startTime: string;
  endTime: string;
  eventType: 'tour' | 'phone' | 'meeting';
  clientUserId: string;
  clientUserName?: string;
  scheduledByName: string;
  scheduledById: string;
}

class NotificationService {
  async sendLeadWithAppointmentNotification(
    leadData: LeadNotificationData,
    appointmentData: AppointmentNotificationData
  ): Promise<void> {
    try {
      // Get project details
      const project = await projectService.getProject(leadData.projectId);
      if (!project) {
        return;
      }

      const projectName = project.name || 'Project';
      const projectTimezone = project.settings?.timezone || '';
      const formattedTime = formatTime(appointmentData.startTime, projectTimezone);
      const formattedRange = formatTimeRange(appointmentData.startTime, appointmentData.endTime, projectTimezone);

      // Get client user info
      let clientUserName = appointmentData.clientUserName || 'Client';
      if (!clientUserName && appointmentData.clientUserId) {
        const clientUserDoc = await getDoc(doc(db, 'users', appointmentData.clientUserId));
        if (clientUserDoc.exists()) {
          const userData = clientUserDoc.data();
          clientUserName = userData.firstName
            ? `${userData.firstName} ${userData.lastName || ''}`.trim()
            : 'Client';
        }
      }

      // Find provider_admin and provider_user from project assignedUsers
      const providerUsers = project.assignedUsers?.filter(
        (u: any) => u.role === 'provider_admin' || u.role === 'provider_user'
      ) || [];

      // Create combined notification for each provider user
      for (const provider of providerUsers) {
        if (provider.userId === leadData.capturedById) continue; // Don't notify self

        const notifRef = doc(collection(db, 'notifications'));
        await setDoc(notifRef, {
          id: notifRef.id,
          userId: provider.userId,
          type: 'lead_with_appointment',
          title: 'New Lead with Appointment Scheduled',
          message: `${leadData.capturedByName} captured lead "${leadData.leadName}" and scheduled a ${appointmentData.eventType} for ${appointmentData.date} at ${formattedTime} with ${clientUserName}`,
          data: {
            leadId: leadData.leadId,
            leadName: leadData.leadName,
            appointmentId: appointmentData.appointmentId,
            appointmentDate: appointmentData.date,
            appointmentTime: formattedTime,
            eventType: appointmentData.eventType,
            projectId: leadData.projectId,
            projectName,
            clientUserId: appointmentData.clientUserId,
            clientUserName,
            capturedBy: leadData.capturedByName
          },
          read: false,
          createdAt: Timestamp.now()
        });
      }

      // Notify client user (the one being scheduled with)
      if (appointmentData.clientUserId && appointmentData.clientUserId !== leadData.capturedById) {
        const clientNotifRef = doc(collection(db, 'notifications'));
        await setDoc(clientNotifRef, {
          id: clientNotifRef.id,
          userId: appointmentData.clientUserId,
          type: 'appointment_scheduled',
          title: `New ${appointmentData.eventType === 'tour' ? 'Tour' : 'Call'} Scheduled`,
          message: `${leadData.capturedByName} scheduled a ${appointmentData.eventType} with ${leadData.leadName} for ${appointmentData.date} at ${formattedTime}`,
          data: {
            leadId: leadData.leadId,
            leadName: leadData.leadName,
            appointmentId: appointmentData.appointmentId,
            appointmentDate: appointmentData.date,
            appointmentTime: formattedTime,
            eventType: appointmentData.eventType,
            projectId: leadData.projectId,
            projectName
          },
          read: false,
          createdAt: Timestamp.now()
        });

        // Push to client user's connected calendars
        try {
          const startDateTime = new Date(`${appointmentData.date}T${appointmentData.startTime}`);
          const endDateTime = new Date(`${appointmentData.date}T${appointmentData.endTime}`);

          await CalendarService.syncAppointmentToCalendars(appointmentData.clientUserId, {
            id: appointmentData.appointmentId,
            title: `${appointmentData.eventType === 'tour' ? 'Tour' : 'Call'}: ${leadData.leadName}`,
            description: `${appointmentData.eventType === 'tour' ? 'Tour' : 'Follow-up call'} with ${leadData.leadName}\nPhone: ${leadData.phone || 'N/A'}\nScheduled by: ${leadData.capturedByName}`,
            startDate: startDateTime,
            endDate: endDateTime,
            eventType: appointmentData.eventType,
            attendees: leadData.email ? [{ email: leadData.email, name: leadData.leadName }] : undefined
          });
        } catch (calendarError) {
          console.error('Error syncing to calendar:', calendarError);
        }
      }

      // Notify field user who captured the lead (confirmation)
      const fieldUserNotifRef = doc(collection(db, 'notifications'));
      await setDoc(fieldUserNotifRef, {
        id: fieldUserNotifRef.id,
        userId: leadData.capturedById,
        type: 'lead_confirmed',
        title: 'Lead Captured with Appointment',
        message: `Lead "${leadData.leadName}" captured with ${appointmentData.eventType} scheduled for ${appointmentData.date} at ${formattedTime}`,
        data: {
          leadId: leadData.leadId,
          leadName: leadData.leadName,
          appointmentId: appointmentData.appointmentId,
          appointmentDate: appointmentData.date,
          appointmentTime: formattedTime,
          eventType: appointmentData.eventType,
          projectId: leadData.projectId,
          projectName
        },
        read: false,
        createdAt: Timestamp.now()
      });

    } catch (error) {
      console.error('Error sending combined notifications:', error);
    }
  }

  async sendLeadOnlyNotification(leadData: LeadNotificationData): Promise<void> {
    try {
      const project = await projectService.getProject(leadData.projectId);
      if (!project) {
        return;
      }

      const projectName = project.name || 'Project';

      // Find all users to notify (provider_admin, provider_user, client_admin, client_user)
      const usersToNotify = project.assignedUsers?.filter(
        (u: any) => ['provider_admin', 'provider_user', 'client_admin', 'client_user'].includes(u.role)
      ) || [];

      // Create notification for each user
      for (const user of usersToNotify) {
        if (user.userId === leadData.capturedById) continue; // Don't notify self

        const notifRef = doc(collection(db, 'notifications'));
        await setDoc(notifRef, {
          id: notifRef.id,
          userId: user.userId,
          type: 'lead_submitted',
          title: 'New Lead Submitted',
          message: `${leadData.capturedByName} submitted a new lead: ${leadData.leadName} in ${projectName}`,
          data: {
            leadId: leadData.leadId,
            leadName: leadData.leadName,
            projectId: leadData.projectId,
            projectName,
            capturedBy: leadData.capturedByName,
            phone: leadData.phone,
            email: leadData.email
          },
          read: false,
          createdAt: Timestamp.now()
        });
      }

    } catch (error) {
      console.error('Error sending lead notifications:', error);
    }
  }

  async sendFollowUpAppointmentNotification(
    appointmentData: AppointmentNotificationData
  ): Promise<void> {
    try {
      const project = await projectService.getProject(appointmentData.projectId);
      if (!project) {
        return;
      }

      const projectName = appointmentData.projectName || project.name || 'Project';
      const projectTimezone = project.settings?.timezone || '';
      const formattedTime = formatTime(appointmentData.startTime, projectTimezone);

      // Get client user info
      let clientUserName = appointmentData.clientUserName || 'Client';
      if (!clientUserName && appointmentData.clientUserId) {
        const clientUserDoc = await getDoc(doc(db, 'users', appointmentData.clientUserId));
        if (clientUserDoc.exists()) {
          const userData = clientUserDoc.data();
          clientUserName = userData.firstName
            ? `${userData.firstName} ${userData.lastName || ''}`.trim()
            : 'Client';
        }
      }

      // Find provider users
      const providerUsers = project.assignedUsers?.filter(
        (u: any) => u.role === 'provider_admin' || u.role === 'provider_user'
      ) || [];

      // Notify provider users
      for (const provider of providerUsers) {
        if (provider.userId === appointmentData.scheduledById) continue;

        const notifRef = doc(collection(db, 'notifications'));
        await setDoc(notifRef, {
          id: notifRef.id,
          userId: provider.userId,
          type: 'followup_scheduled',
          title: 'Follow-up Appointment Scheduled',
          message: `${appointmentData.scheduledByName} scheduled a follow-up ${appointmentData.eventType} with ${appointmentData.leadName} for ${appointmentData.date} at ${formattedTime}`,
          data: {
            leadId: appointmentData.leadId,
            leadName: appointmentData.leadName,
            appointmentId: appointmentData.appointmentId,
            appointmentDate: appointmentData.date,
            appointmentTime: formattedTime,
            eventType: appointmentData.eventType,
            projectId: appointmentData.projectId,
            projectName,
            clientUserId: appointmentData.clientUserId,
            clientUserName
          },
          read: false,
          createdAt: Timestamp.now()
        });
      }

      // Notify client user
      if (appointmentData.clientUserId && appointmentData.clientUserId !== appointmentData.scheduledById) {
        const clientNotifRef = doc(collection(db, 'notifications'));
        await setDoc(clientNotifRef, {
          id: clientNotifRef.id,
          userId: appointmentData.clientUserId,
          type: 'followup_scheduled',
          title: `Follow-up ${appointmentData.eventType === 'tour' ? 'Tour' : 'Call'} Scheduled`,
          message: `${appointmentData.scheduledByName} scheduled a follow-up ${appointmentData.eventType} with ${appointmentData.leadName} for ${appointmentData.date} at ${formattedTime}`,
          data: {
            leadId: appointmentData.leadId,
            leadName: appointmentData.leadName,
            appointmentId: appointmentData.appointmentId,
            appointmentDate: appointmentData.date,
            appointmentTime: formattedTime,
            eventType: appointmentData.eventType,
            projectId: appointmentData.projectId,
            projectName
          },
          read: false,
          createdAt: Timestamp.now()
        });

        // Push to calendar
        try {
          const startDateTime = new Date(`${appointmentData.date}T${appointmentData.startTime}`);
          const endDateTime = new Date(`${appointmentData.date}T${appointmentData.endTime}`);

          await CalendarService.syncAppointmentToCalendars(appointmentData.clientUserId, {
            id: appointmentData.appointmentId,
            title: `Follow-up ${appointmentData.eventType === 'tour' ? 'Tour' : 'Call'}: ${appointmentData.leadName}`,
            description: `Follow-up ${appointmentData.eventType === 'tour' ? 'tour' : 'call'} with ${appointmentData.leadName}\nScheduled by: ${appointmentData.scheduledByName}`,
            startDate: startDateTime,
            endDate: endDateTime,
            eventType: appointmentData.eventType
          });
        } catch (calendarError) {
          console.error('Error syncing to calendar:', calendarError);
        }
      }

    } catch (error) {
      console.error('Error sending follow-up notifications:', error);
    }
  }
}

export const notificationService = new NotificationService();
